import { useEffect, useState } from "react";
import api from "@/lib/api";
import { STATUS_ORDER, EXCEL_STATUSES, APP_STATUSES, statusInfo } from "@/constants/statuses";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  CartesianGrid,
} from "recharts";
import { Loader2 } from "lucide-react";

export default function StatsView() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/meters/stats").then((r) => setStats(r.data));
  }, []);

  if (!stats)
    return (
      <div className="flex items-center justify-center h-full text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement des statistiques…
      </div>
    );

  const kpis = [
    { key: "total", label: "Total compteurs", value: stats.total, color: "#00A896" },
    { key: "a_prendre_en_charge", label: "À prendre en charge", value: stats.by_status.a_prendre_en_charge, color: statusInfo("a_prendre_en_charge").color },
    { key: "transmis", label: "Transmis", value: stats.by_status.transmis, color: statusInfo("transmis").color },
    { key: "termine", label: "Terminé", value: stats.by_status.termine, color: statusInfo("termine").color },
  ];

  const npaData = stats.by_npa.slice(0, 10).map((d) => ({ name: d.label, count: d.count }));
  const progress = stats.total ? Math.round(((stats.by_status.termine || 0) / stats.total) * 100) : 0;

  return (
    <div className="p-4 lg:p-6 overflow-y-auto h-full" data-testid="stats-view">
      <h2 className="font-display text-xl font-bold mb-1">Statistiques</h2>
      <p className="text-sm text-slate-500 mb-6">Vue d'ensemble de l'avancement des remplacements.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {kpis.map((k) => (
          <div
            key={k.key}
            className="rounded-xl border border-slate-800 bg-slate-900/60 p-5"
            data-testid={`kpi-${k.key}`}
          >
            <p className="text-xs uppercase tracking-wider text-slate-500">{k.label}</p>
            <p className="font-display text-3xl font-extrabold mt-2" style={{ color: k.color }}>
              {k.value}
            </p>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5 mb-6">
        <div className="flex items-center justify-between mb-2">
          <p className="font-semibold text-sm">Taux d'avancement</p>
          <p className="font-mono-data text-sm text-emerald-400">{progress}%</p>
        </div>
        <div className="h-3 rounded-full bg-slate-800 overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} />
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          {EXCEL_STATUSES.map((k) => (
            <div key={k} className="flex items-center gap-2 text-xs text-slate-400">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: statusInfo(k).color }} />
              {statusInfo(k).label}: <span className="text-slate-200 font-medium">{stats.by_status[k]}</span>
            </div>
          ))}
          {APP_STATUSES.map((k) => (
            <div key={k} className="flex items-center gap-2 text-xs text-slate-400">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: statusInfo(k).color }} />
              {statusInfo(k).label} <span className="text-[10px] text-slate-500">(app)</span>: <span className="text-slate-200 font-medium">{(stats.by_status_app || {})[k] || 0}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
        <p className="font-semibold text-sm mb-4">Compteurs par zone (NPA / Lieu)</p>
        <ResponsiveContainer width="100%" height={340}>
          <BarChart data={npaData} layout="vertical" margin={{ left: 30, right: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
            <XAxis type="number" stroke="#64748b" fontSize={11} />
            <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={11} width={110} />
            <Tooltip
              cursor={{ fill: "#1e293b55" }}
              contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, color: "#f8fafc" }}
            />
            <Bar dataKey="count" radius={[0, 6, 6, 0]}>
              {npaData.map((_, i) => (
                <Cell key={i} fill="#00A896" />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
